import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import type { OutreachHome } from '../outreachTypes';
import { outreachBuilderPath } from '../outreachPaths';
import { planRate } from '../campaigns/planRates';
import { homeMessages as messages } from './homeMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Home.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** "Out in 5 days" counts only for a song that comes out in the next 4 weeks. */
const SOON_DAYS = 28;

/**
 * The first visit of the Pitch home (design outreach-v2 i2): no campaign yet. What Plug does, the
 * three steps, and "Pitch <song>" with the song of `home.pitchSong`. Free sees the same page; the
 * Start step of the setup opens the plans.
 */
const HomeFirstPitchComponent: React.FC<{ pitchSong: OutreachHome['pitchSong'] }> = ({ pitchSong }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const steps = useMemo(
    () => [
      { key: '1', title: messages.firstStep1Title, body: intl.formatMessage(messages.firstStep1Body) },
      { key: '2', title: messages.firstStep2Title, body: intl.formatMessage(messages.firstStep2Body) },
      {
        key: '3',
        title: messages.firstStep3Title,
        body: intl.formatMessage(messages.firstStep3Body, { pro: planRate('pro'), label: planRate('label') }),
      },
    ],
    [intl]
  );
  // eslint-disable-next-line no-void
  const open = (song: string | null) => void navigate(outreachBuilderPath({ song }));
  const days = pitchSong?.daysToRelease ?? null;

  return (
    <section className={styles.first} data-testid="home-first-pitch">
      <span className={shared.eyebrow}>
        <span className={shared.dot} />
        {intl.formatMessage(messages.firstEyebrow)}
      </span>
      <h2>{intl.formatMessage(messages.firstTitle)}</h2>
      <p className={shared.sub}>{intl.formatMessage(messages.firstBody)}</p>
      <div className={styles.firstSteps}>
        {steps.map((step) => (
          <div key={step.key} className={styles.firstStep}>
            <b>{intl.formatMessage(step.title)}</b>
            {step.body}
          </div>
        ))}
      </div>
      <div className={shared.row}>
        {pitchSong === null ? (
          <SMLButton color="primary" omitCap leftIcon={faPlus} onClick={() => open(null)} testID="home-first-pick">
            {intl.formatMessage(messages.firstPickSong)}
          </SMLButton>
        ) : (
          <>
            <SMLButton
              color="primary"
              omitCap
              leftIcon={faPlus}
              onClick={() => open(pitchSong.catalogItemUuid)}
              testID="home-first-pitch-song"
            >
              {intl.formatMessage(messages.firstPitchSong, { song: pitchSong.title })}
            </SMLButton>
            <SMLButton color="link" omitCap onClick={() => open(null)}>
              {intl.formatMessage(messages.firstPickAnother)}
            </SMLButton>
            {days !== null && days >= 0 && days <= SOON_DAYS && (
              <span className={shared.small}>
                {intl.formatMessage(messages.firstSoon, { song: pitchSong.title, days })}
              </span>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export const HomeFirstPitch = TypedMemo(HomeFirstPitchComponent);
